// Thao tác của kỳ lương. Mọi thao tác đổi trạng thái đọc lại kỳ và dữ liệu nguồn mới nhất rồi kiểm điều
// kiện bằng chính các hàm trong core/periodWorkbench, không tin số đang hiện trên màn hình. Không có
// transaction nên ghi chi tiết trước, trạng thái kỳ sau cùng; lỗi giữa chừng thì làm lại là đủ.
import { formatPayrollCode, getPayrollDateRange } from '../core/domain';
import { createPeriodTemplateCompiler, resolvePeriodEmployees, summarizeProductivityLines } from '../core/periodInputs';
import {
    buildDetailRow, buildDetailSnapshot, buildPeriodCalculation, computePeriodInputHash, diffPeriodEmployees,
    evaluateReadiness, isPeriodEditable, periodRange, summarizeWarnings,
} from '../core/periodWorkbench';
import { writeAudit } from './configActions';
import { loadPayrollReferenceData, loadPeriodProductivity } from './periodLoader';
import { assertCanManagePayroll, currentPayrollUser } from './permissions';
import { payrollRepos } from './tables';

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export async function listTemplates() {
    return payrollRepos.templates.list();
}

/**
 * Sửa nhanh xung đột mẫu: đưa nhân sự vào danh sách loại trừ của một mẫu để họ chỉ còn khớp mẫu kia.
 * Mẫu đã khóa thì không sửa được.
 */
export async function excludeEmployeeFromTemplate(templateCode, soHd) {
    assertCanManagePayroll();
    const [template] = await payrollRepos.templates.listWhere('MA_MAU', templateCode);
    if (!template) throw new Error(`Không tìm thấy mẫu ${templateCode}.`);
    if (template.KHOA_THAY_DOI) throw new Error(`Mẫu ${template.TEN_MAU} đã khóa, không sửa phạm vi được. Hãy sửa ở mẫu còn lại.`);
    await payrollRepos.templates.update([{
        MA_MAU: templateCode,
        NHAN_SU_LOAI_TRU: [...new Set([...(template.NHAN_SU_LOAI_TRU ?? []), soHd])],
        NHAN_SU_LIST: (template.NHAN_SU_LIST ?? []).filter((item) => item !== soHd),
    }]);
    await writeAudit('Loại nhân sự khỏi mẫu', `${soHd} khỏi ${templateCode}`);
}

export async function listPeriods() {
    const periods = await payrollRepos.periods.list();
    return periods.sort((left, right) => (right.NAM - left.NAM) || (right.THANG - left.THANG));
}

async function readPeriod(maKy) {
    const [period] = await payrollRepos.periods.listWhere('MA_KY', maKy);
    if (!period) throw new Error(`Không tìm thấy kỳ lương ${maKy}.`);
    return period;
}

function assertEditable(period) {
    if (!isPeriodEditable(period)) throw new Error('Kỳ lương không ở trạng thái cho phép chỉnh sửa (chỉ Nháp hoặc Từ chối).');
}

// ===== TẠO KỲ =====

/** Xem trước kỳ: ai thuộc mẫu nào, ai xung đột, ai không có mẫu. */
export async function previewPeriodDraft(month, year, templateCodes) {
    const range = getPayrollDateRange(month, year);
    const ref = await loadPayrollReferenceData(range, formatPayrollCode(month, year));
    const selectedTemplates = ref.templates.filter((template) => template.HOAT_DONG && templateCodes.includes(template.MA_MAU));
    const resolved = resolvePeriodEmployees({
        employees: ref.employees,
        teamsByEmployee: ref.teamsByEmployee,
        agreementByEmployee: ref.base.agreementByEmployee,
        selectedTemplates,
        allTemplates: ref.templates,
    });
    return { range, ref, resolved, selectedTemplates };
}

const detailRowFor = (maKy, employee, ref, inputs) => buildDetailRow(maKy, employee, ref.employees, inputs);

/** Tạo kỳ. inputs: số đã nhập sẵn trên bảng xem trước, { [soHd]: { [code]: number } }. */
export async function createPeriod({ month, year, templateCodes, note, inputs = {} }, onProgress) {
    assertCanManagePayroll();
    const maKy = formatPayrollCode(month, year);
    const existing = await payrollRepos.periods.listWhere('MA_KY', maKy);
    if (existing.length) throw new Error(`Kỳ lương ${maKy} đã tồn tại.`);
    const draft = await previewPeriodDraft(month, year, templateCodes);
    if (!draft.selectedTemplates.length) throw new Error('Chọn ít nhất một mẫu bảng lương.');
    if (draft.resolved.conflicts.length) throw new Error(`Còn ${draft.resolved.conflicts.length} nhân sự khớp nhiều mẫu cùng lúc. Sửa phạm vi mẫu trước khi tạo kỳ.`);
    if (!draft.resolved.employees.length) throw new Error('Không có nhân sự nào thuộc các mẫu đã chọn.');
    // Làm lại sau một lần lỗi: dòng chi tiết đã ghi thì giữ, chỉ thêm phần còn thiếu.
    const already = new Set((await payrollRepos.periodDetails.listWhere('BANG_ID', maKy)).map((detail) => detail.NHAN_SU_ID));
    const rows = draft.resolved.employees.filter((employee) => !already.has(employee.employeeId)).map((employee) => detailRowFor(maKy, employee, draft.ref, inputs[employee.employeeId] ?? {}));
    await payrollRepos.periodDetails.add(rows, onProgress);
    const header = {
        MA_KY: maKy,
        THANG: month,
        NAM: year,
        TU_NGAY: draft.range.from,
        DEN_NGAY: startOfDay(draft.range.to),
        TRANG_THAI: 'NHAP',
        MAU_IDS: draft.selectedTemplates.map((template) => template.MA_MAU),
        NGUOI_TAO: currentPayrollUser().username,
        TONG_LUONG: 0, TONG_UNG: 0, TONG_BHXH: 0, TONG_THUC_CHI: 0,
        INPUT_HASH: '',
        CANH_BAO_XAC_NHAN: false,
        GHI_CHU: note || '',
    };
    await payrollRepos.periods.add([header]);
    await writeAudit('Tạo kỳ lương', `${draft.resolved.employees.length} nhân sự, mẫu ${header.MAU_IDS.join(', ')}`, maKy);
    return header;
}

// ===== MÀN NHẬP VÀ TÍNH =====

/**
 * Nạp kỳ và mọi dữ liệu nguồn, tính lại bằng dữ liệu đã lưu, so chữ ký với lần chốt gần nhất.
 * calcError: lỗi công thức hoặc mẫu (vd chia cho 0) để màn hình báo thay vì trắng trang.
 */
export async function loadPeriodWorkbench(maKy) {
    const period = await readPeriod(maKy);
    const [details, ref] = await Promise.all([
        payrollRepos.periodDetails.listWhere('BANG_ID', maKy),
        loadPayrollReferenceData(periodRange(period), maKy),
    ]);
    return assembleWorkbench(period, details, ref);
}

function assembleWorkbench(period, details, ref) {
    const templates = ref.templates;
    const currentHash = computePeriodInputHash({ period, details, ref, templates });
    let calculation = null;
    let calcError = null;
    try {
        calculation = buildPeriodCalculation({ period, details, ref, templates }).calculation;
    } catch (error) {
        calcError = error.message;
    }
    const readiness = evaluateReadiness({ period, currentHash, calculation, refWarnings: ref.warnings });
    if (calcError) readiness.blocking.unshift(calcError);
    return {
        period, details, ref, templates, currentHash, calculation, calcError, readiness,
        stale: Boolean(period.INPUT_HASH) && period.INPUT_HASH !== currentHash,
    };
}

/** Lưu số nhập trên bảng. changes: { [soHd]: { [code]: number | null } } (null = xóa số đã nhập). */
export async function savePeriodInputs(maKy, changes) {
    assertCanManagePayroll();
    assertEditable(await readPeriod(maKy));
    const details = await payrollRepos.periodDetails.listWhere('BANG_ID', maKy);
    const detailBySoHd = new Map(details.map((detail) => [detail.NHAN_SU_ID, detail]));
    const rows = Object.entries(changes).flatMap(([soHd, change]) => {
        const detail = detailBySoHd.get(soHd);
        if (!detail) return [];
        const merged = { ...(detail.GIA_TRI_NHAP ?? {}) };
        for (const [code, value] of Object.entries(change)) {
            if (value === null || value === undefined || value === '') delete merged[code];
            else merged[code] = value;
        }
        return [{ ID: detail.ID, GIA_TRI_NHAP: merged }];
    });
    await payrollRepos.periodDetails.update(rows);
    await writeAudit('Lưu nhập liệu kỳ lương', `${rows.length} nhân sự`, maKy);
    return rows.length;
}

/** Kỳ có mẫu nào dùng lương sản phẩm không; không có thì chốt số khỏi phải tính năng suất (chậm nhất). */
function periodUsesProductivity(workbench) {
    const compile = createPeriodTemplateCompiler(workbench.ref.variableCatalog, workbench.ref.base.formulaRows);
    const used = new Set(workbench.details.map((detail) => detail.MAU_ID));
    return workbench.templates.filter((template) => used.has(template.ID))
        .some((template) => compile(template).columns.some((column) => column.source === 'NANG_SUAT'));
}

const sumAmount = (lines) => lines.reduce((total, line) => total + (line.THANH_TIEN ?? line.thanhTien ?? 0), 0);

/**
 * Xem trước lần chốt số (không ghi gì): nạp năng suất của kỳ theo đúng phép tính báo cáo năng suất,
 * tính lại cả kỳ với năng suất mới, so với năng suất đang lưu. Nạp năng suất là một phần của chốt số
 * chứ không phải bước riêng, để người dùng không phải nhớ bấm hai nút theo đúng thứ tự.
 */
/** Năng suất của kỳ tính lại từ nguồn, theo từng dòng chi tiết; chưa ghi gì. */
async function collectPeriodProductivity(workbench) {
    const { details } = workbench;
    const loaded = await loadPeriodProductivity(periodRange(workbench.period));
    const inPeriod = new Set(details.map((detail) => detail.NHAN_SU_ID));
    const rows = details.map((detail) => ({ ID: detail.ID, CHI_TIET_NANG_SUAT: summarizeProductivityLines(loaded.linesByEmployee.get(detail.NHAN_SU_ID) ?? []) }));
    const rowById = new Map(rows.map((row) => [row.ID, row.CHI_TIET_NANG_SUAT]));
    return {
        rows,
        changed: details.filter((detail) => JSON.stringify(detail.CHI_TIET_NANG_SUAT ?? []) !== JSON.stringify(rowById.get(detail.ID))).length,
        people: rows.filter((row) => row.CHI_TIET_NANG_SUAT.length).length,
        total: Math.round(rows.reduce((total, row) => total + sumAmount(row.CHI_TIET_NANG_SUAT), 0)),
        unmatchedCount: loaded.unmatchedRecords.length,
        unmatchedAmount: Math.round(sumAmount(loaded.unmatchedRecords)),
        outside: Array.from(loaded.linesByEmployee.entries()).filter(([soHd]) => !inPeriod.has(soHd)).map(([soHd, lines]) => ({ soHd, amount: Math.round(sumAmount(lines)) })),
        mergedCount: loaded.mergedRows.length,
    };
}

/**
 * Nạp năng suất vào kỳ mà chưa chốt số, như nút "Nạp năng suất" của V2: lương sản phẩm hiện ngay trên
 * bảng (số tạm tính) để kiểm tra trước. Chốt số vẫn tự nạp lại, nên bỏ qua bước này cũng không sai.
 */
export async function loadProductivityIntoPeriod(maKy, onProgress) {
    assertCanManagePayroll();
    const workbench = await loadPeriodWorkbench(maKy);
    assertEditable(workbench.period);
    if (!periodUsesProductivity(workbench)) throw new Error('Các mẫu của kỳ này không có lương sản phẩm.');
    const { rows, ...summary } = await collectPeriodProductivity(workbench);
    await payrollRepos.periodDetails.update(rows, onProgress);
    await writeAudit('Nạp năng suất vào kỳ lương', summary, maKy);
    return summary;
}

export async function savePeriodNote(maKy, note) {
    assertCanManagePayroll();
    assertEditable(await readPeriod(maKy));
    await payrollRepos.periods.update([{ MA_KY: maKy, GHI_CHU: note }]);
}

export async function previewPeriodCalculation(maKy) {
    const workbench = await loadPeriodWorkbench(maKy);
    assertEditable(workbench.period);
    let details = workbench.details;
    let productivity = null;
    if (periodUsesProductivity(workbench)) {
        productivity = await collectPeriodProductivity(workbench);
        const rowById = new Map(productivity.rows.map((row) => [row.ID, row.CHI_TIET_NANG_SUAT]));
        details = details.map((detail) => ({ ...detail, CHI_TIET_NANG_SUAT: rowById.get(detail.ID) }));
    }
    const { calculation } = buildPeriodCalculation({ period: workbench.period, details, ref: workbench.ref, templates: workbench.templates });
    return {
        employeeCount: details.length,
        summary: calculation.summary,
        warnings: [...summarizeWarnings(calculation.warnings), ...workbench.ref.warnings],
        productivity,
    };
}

/** Chốt số theo bản xem trước: ghi năng suất vừa nạp (nếu kỳ dùng) rồi tính và ghi kết quả. */
export async function confirmPeriodCalculation(maKy, preview, onProgress) {
    assertCanManagePayroll();
    if (preview.productivity) {
        await payrollRepos.periodDetails.update(preview.productivity.rows, onProgress);
        const { rows, ...summary } = preview.productivity;
        await writeAudit('Nạp năng suất vào kỳ lương', summary, maKy);
    }
    return calculatePeriodAndSave(maKy, onProgress);
}

/** Chốt số: tính bằng dữ liệu đã lưu mới nhất, ghi kết quả từng người rồi tổng và chữ ký của kỳ. */
export async function calculatePeriodAndSave(maKy, onProgress) {
    assertCanManagePayroll();
    const workbench = await loadPeriodWorkbench(maKy);
    assertEditable(workbench.period);
    if (workbench.calcError) throw new Error(workbench.calcError);
    const resultByEmployee = new Map(workbench.calculation.employees.map((employee) => [employee.employeeId, employee]));
    const rows = workbench.details.map((detail) => ({ ID: detail.ID, ...buildDetailSnapshot(resultByEmployee.get(detail.NHAN_SU_ID)) }));
    await payrollRepos.periodDetails.update(rows, onProgress);
    const { summary } = workbench.calculation;
    await payrollRepos.periods.update([{
        MA_KY: maKy,
        TONG_LUONG: summary.totalGross,
        TONG_UNG: summary.totalAdvance,
        TONG_BHXH: summary.totalInsurance,
        TONG_THUC_CHI: summary.totalNet,
        CALCULATED_AT: new Date(),
        INPUT_HASH: workbench.currentHash,
    }]);
    await writeAudit('Chốt số kỳ lương', summary, maKy);
    return summary;
}

/** Ghi đè một ô tính ra (bắt buộc có lý do); value null là bỏ ghi đè. */
export async function overridePeriodValue(maKy, soHd, code, value, reason) {
    assertCanManagePayroll();
    assertEditable(await readPeriod(maKy));
    const [detail] = await payrollRepos.periodDetails.listWhere('ID', `${maKy}|${soHd}`);
    if (!detail) throw new Error('Không tìm thấy nhân sự trong kỳ.');
    const overrides = { ...(detail.GHI_DE ?? {}) };
    if (value === null) {
        delete overrides[code];
    } else {
        if (!String(reason ?? '').trim()) throw new Error('Phải ghi lý do khi ghi đè.');
        if (Number.isNaN(Number(value))) throw new Error('Giá trị ghi đè phải là số.');
        overrides[code] = { value: Number(value), reason: String(reason).trim(), by: currentPayrollUser().username, at: new Date().toISOString() };
    }
    await payrollRepos.periodDetails.update([{ ID: detail.ID, GHI_DE: overrides }]);
    await writeAudit(value === null ? 'Bỏ ghi đè' : 'Ghi đè', `${soHd} ${code}${value === null ? '' : ` = ${value}: ${reason}`}`, maKy);
}

// ===== NHÂN SỰ CỦA KỲ =====

/**
 * So nhân sự của kỳ với nhân sự và phạm vi mẫu hiện tại. templateCodes: bộ mẫu mới khi "Đổi mẫu áp
 * dụng"; bỏ trống là giữ bộ mẫu của kỳ (chỉ cập nhật nhân sự).
 */
export async function previewPeriodEmployeeRefresh(maKy, templateCodes) {
    const period = await readPeriod(maKy);
    const codes = templateCodes ?? period.MAU_IDS;
    const details = await payrollRepos.periodDetails.listWhere('BANG_ID', maKy);
    const draft = await previewPeriodDraft(period.THANG, period.NAM, codes);
    return { period, details, draft, templateCodes: codes, diff: diffPeriodEmployees(details, draft.resolved) };
}

/** Thêm người mới vào, bỏ người không còn thuộc kỳ, đổi mẫu cho người đổi mẫu; ghi bộ mẫu mới của kỳ. */
export async function applyPeriodEmployeeRefresh(maKy, templateCodes) {
    assertCanManagePayroll();
    const { period, draft, diff, templateCodes: codes } = await previewPeriodEmployeeRefresh(maKy, templateCodes);
    assertEditable(period);
    if (!codes.length) throw new Error('Chọn ít nhất một mẫu bảng lương.');
    if (draft.resolved.conflicts.length) throw new Error(`Còn ${draft.resolved.conflicts.length} nhân sự xung đột mẫu, sửa phạm vi mẫu trước.`);
    if (diff.added.length) await payrollRepos.periodDetails.add(diff.added.map((employee) => detailRowFor(maKy, employee, draft.ref)));
    if (diff.changed.length) await payrollRepos.periodDetails.update(diff.changed.map((employee) => ({ ID: `${maKy}|${employee.employeeId}`, MAU_ID: employee.templateId })));
    if (diff.removed.length) await payrollRepos.periodDetails.remove(diff.removed.map((detail) => detail.ID));
    if (JSON.stringify(codes) !== JSON.stringify(period.MAU_IDS)) await payrollRepos.periods.update([{ MA_KY: maKy, MAU_IDS: codes }]);
    await writeAudit('Cập nhật nhân sự kỳ lương', { mau: codes, them: diff.added.length, bo: diff.removed.length, doiMau: diff.changed.length }, maKy);
    return diff;
}

// ===== QUY TRÌNH DUYỆT =====

/** Gửi duyệt. Có cảnh báo mà chưa xác nhận thì trả về danh sách để màn hình hỏi lại. */
export async function submitPeriod(maKy, confirmWarnings = false) {
    assertCanManagePayroll();
    const workbench = await loadPeriodWorkbench(maKy);
    assertEditable(workbench.period);
    const { blocking, warnings } = workbench.readiness;
    if (blocking.length) throw new Error(blocking.join('\n'));
    if (warnings.length && !confirmWarnings) return { requiresConfirmation: true, warnings };
    const user = currentPayrollUser().username;
    await payrollRepos.periods.update([{ MA_KY: maKy, TRANG_THAI: 'CHO_DUYET', NGUOI_GUI: user, NGAY_GUI: new Date(), CANH_BAO_XAC_NHAN: warnings.length > 0, LY_DO_TU_CHOI: '' }]);
    await writeAudit('Gửi duyệt kỳ lương', warnings.length ? `Đã xác nhận ${warnings.length} cảnh báo` : 'Không có cảnh báo', maKy);
    return { submitted: true };
}

/**
 * Duyệt: người lập không tự duyệt; số phải còn khớp dữ liệu nguồn. Phiếu ứng của kỳ chuyển Đã trừ lương,
 * mẫu đã dùng bị khóa, trạng thái kỳ ghi sau cùng.
 */
export async function approvePeriod(maKy) {
    assertCanManagePayroll();
    const workbench = await loadPeriodWorkbench(maKy);
    const { period, details, ref, readiness } = workbench;
    if (period.TRANG_THAI !== 'CHO_DUYET') throw new Error('Chỉ duyệt được kỳ đang chờ duyệt.');
    const user = currentPayrollUser().username;
    if (period.NGUOI_TAO && period.NGUOI_TAO === user) throw new Error('Người lập không được tự duyệt bảng lương của mình.');
    if (readiness.blocking.length) throw new Error(readiness.blocking.join('\n'));
    if (readiness.warnings.length && !period.CANH_BAO_XAC_NHAN) throw new Error('Người gửi chưa xác nhận các cảnh báo của kỳ lương.');
    const employeeIds = new Set(details.map((detail) => detail.NHAN_SU_ID));
    const advances = ref.base.advances.filter((advance) => employeeIds.has(advance.NHAN_SU_ID));
    if (advances.length) {
        await payrollRepos.advances.update(advances.map((advance) => ({ MA_PHIEU: advance.MA_PHIEU, TRANG_THAI: 'DA_SNAPSHOT', BANG_CONG_LUONG_ID: maKy, SO_TIEN_SNAPSHOT: advance.SO_TIEN })));
    }
    const templateIds = [...new Set(details.map((detail) => detail.MAU_ID))];
    await payrollRepos.templates.update(templateIds.map((id) => ({ MA_MAU: id, KHOA_THAY_DOI: true })));
    await payrollRepos.periods.update([{ MA_KY: maKy, TRANG_THAI: 'DA_DUYET', NGUOI_DUYET: user, NGAY_DUYET: new Date() }]);
    await writeAudit('Duyệt kỳ lương', `${details.length} nhân sự, ${advances.length} phiếu ứng`, maKy);
}

export async function rejectPeriod(maKy, reason) {
    assertCanManagePayroll();
    if (!String(reason ?? '').trim()) throw new Error('Phải ghi lý do từ chối.');
    const period = await readPeriod(maKy);
    if (period.TRANG_THAI !== 'CHO_DUYET') throw new Error('Chỉ từ chối được kỳ đang chờ duyệt.');
    await payrollRepos.periods.update([{ MA_KY: maKy, TRANG_THAI: 'TU_CHOI', LY_DO_TU_CHOI: String(reason).trim(), NGUOI_DUYET: currentPayrollUser().username }]);
    await writeAudit('Từ chối kỳ lương', reason, maKy);
}

/** Mở lại kỳ đã duyệt: trả phiếu ứng, xóa số đã chốt, về Nháp. Mẫu giữ khóa vì kỳ khác có thể đã dùng. */
export async function reopenPeriod(maKy, reason, onProgress) {
    assertCanManagePayroll();
    if (!String(reason ?? '').trim()) throw new Error('Phải ghi lý do mở lại.');
    const period = await readPeriod(maKy);
    if (period.TRANG_THAI !== 'DA_DUYET') throw new Error('Chỉ mở lại được kỳ đã duyệt.');
    const [advances, details] = await Promise.all([
        payrollRepos.advances.listWhere('BANG_CONG_LUONG_ID', maKy),
        payrollRepos.periodDetails.listWhere('BANG_ID', maKy),
    ]);
    if (advances.length) {
        await payrollRepos.advances.update(advances.map((advance) => ({ MA_PHIEU: advance.MA_PHIEU, TRANG_THAI: 'CHUA_SU_DUNG', BANG_CONG_LUONG_ID: '', SO_TIEN_SNAPSHOT: null })));
    }
    await payrollRepos.periodDetails.update(details.map((detail) => ({ ID: detail.ID, GIA_TRI_TINH: {}, TONG_LUONG: 0, TONG_UNG: 0, BH_NLD: 0, TNCN: 0, TONG_THUC_CHI: 0 })), onProgress);
    await payrollRepos.periods.update([{
        MA_KY: maKy, TRANG_THAI: 'NHAP', NGUOI_MO_LAI: currentPayrollUser().username, NGAY_MO_LAI: new Date(), LY_DO_MO_LAI: String(reason).trim(),
        TONG_LUONG: 0, TONG_UNG: 0, TONG_BHXH: 0, TONG_THUC_CHI: 0, CALCULATED_AT: null, INPUT_HASH: '', CANH_BAO_XAC_NHAN: false,
    }]);
    await writeAudit('Mở lại kỳ lương', reason, maKy);
}

export async function deletePeriod(maKy, onProgress) {
    assertCanManagePayroll();
    const period = await readPeriod(maKy);
    assertEditable(period);
    const details = await payrollRepos.periodDetails.listWhere('BANG_ID', maKy);
    if (details.length) await payrollRepos.periodDetails.remove(details.map((detail) => detail.ID), onProgress);
    await payrollRepos.periods.remove([maKy]);
    await writeAudit('Xóa kỳ lương', `${details.length} nhân sự`, maKy);
}

export async function loadPeriodAudit(maKy) {
    const rows = await payrollRepos.auditLog.listWhere('MA_KY', maKy);
    return rows.sort((left, right) => (right.THOI_GIAN?.getTime() ?? 0) - (left.THOI_GIAN?.getTime() ?? 0));
}
